import SEOHead from '@/components/base/SEOHead';
import Layout from '@/components/feature/Layout';
import CTASection from '@/components/feature/CTASection';
import { Link } from 'react-router-dom';

interface GuideLayoutProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  h1: string;
  subtitle?: string;
  publishDate?: string;
  modifiedDate?: string;
  children: React.ReactNode;
  relatedFeatures?: { label: string; path: string }[];
  relatedGuides?: { label: string; path: string }[];
}

export default function GuideLayout({
  title,
  description,
  keywords,
  canonical,
  h1,
  subtitle,
  publishDate = '2026-07-18',
  modifiedDate = '2026-07-18',
  children,
  relatedFeatures,
  relatedGuides,
}: GuideLayoutProps) {
  const articleData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': h1,
    'description': description,
    'datePublished': publishDate,
    'dateModified': modifiedDate,
    'author': {
      '@type': 'Organization',
      'name': '株式会社cafoot',
    },
  };

  return (
    <Layout>
      <SEOHead title={title} description={description} keywords={keywords} canonical={canonical} ogType="article" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleData) }} />

      <article className="py-12 md:py-16">
        <div className="max-w-[960px] mx-auto px-4 md:px-6">
          <header className="mb-10">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4">{h1}</h1>
            {subtitle && <p className="text-base text-foreground-600 leading-relaxed mb-4">{subtitle}</p>}
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span>公開日: {publishDate}</span>
              <span>更新日: {modifiedDate}</span>
            </div>
          </header>

          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700">
            {children}
          </div>

          {(relatedFeatures || relatedGuides) && (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedFeatures && relatedFeatures.length > 0 && (
                <div className="p-6 bg-background-100 rounded-lg border border-background-200/70">
                  <h2 className="text-base font-bold text-foreground-900 mb-3">関連する機能</h2>
                  <ul className="space-y-2">
                    {relatedFeatures.map((item) => (
                      <li key={item.path}>
                        <Link to={item.path} className="text-sm text-primary-600 hover:text-primary-700 inline-flex items-center gap-1">
                          <i className="ri-arrow-right-s-line"></i>
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {relatedGuides && relatedGuides.length > 0 && (
                <div className="p-6 bg-background-100 rounded-lg border border-background-200/70">
                  <h2 className="text-base font-bold text-foreground-900 mb-3">関連するお役立ち記事</h2>
                  <ul className="space-y-2">
                    {relatedGuides.map((item) => (
                      <li key={item.path}>
                        <Link to={item.path} className="text-sm text-primary-600 hover:text-primary-700 inline-flex items-center gap-1">
                          <i className="ri-arrow-right-s-line"></i>
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </article>

      <CTASection title="プロジェクト管理の効率化を、AIではじめませんか？" />
    </Layout>
  );
}